/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts Conversation UI
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import DashboardSpec from './DashboardSpec'

/**
 * Converts content recommendation to an intermediate object that maps more easily to DashboardAPI.
 */
export default {

	convertToTabContents(glassContext, options) {

		this.glassContext = glassContext
		const id = 'dashboard_' + Date.now()
		const { sourceId, sourceType, modelRefId } = options
		const sourceInfo = { modelRefId, sourceId, sourceType }
		return this.processSmartsContent(glassContext, options)
			.then( tabs => DashboardSpec.buildTabContents(id, tabs, sourceInfo))

	},

	processSmartsContent(glassContext, { sourceId, sourceType, content, modelRefId }){
		this.glassContext = glassContext
		return Promise.all( content.map( container => this._processContainer( container, sourceId, sourceType, modelRefId )))
	},

	_processContainer( container, sourceId, sourceType, modelRefId  ){
		const { groups, predictive:hasPredictChart, label:tabLabel } = container
		return Promise.all( groups.map(({ contents }, index) => this._processGroup(sourceId, sourceType, modelRefId, contents, index == 0, hasPredictChart)))
			.then( chartInfoListGroups => {
				let chartInfoList = chartInfoListGroups.reduce( (flattenList, chartInfoList ) => flattenList.concat(chartInfoList), [])
				return { chartInfoList, tabLabel }
			})
	},

	/**
	 * For each container, there is a primary group and secondary group
	 * Primary group could consist just of viz, viz and text, predict viz and viz, or predict, viz and text
	 * Secondary group contains only viz
	 * Each group is organized vertically on the page
	 * @param content
	 * @returns {*}
	 * @private
	 */
	_processGroup(sourceId, sourceType, modelRefId, groupContents, isPrimaryGroup, hasPredict ){
		return Promise.all( groupContents.map(({ chart }) =>
			/*
				The conversion to livewidget spec has to be done before generating the layout info to take into
			 	account that the conversion may fail to generate a spec, e.g. a chart may be supported
			 	but not the column binding.
			 */
			this._convertToLiveWidgetSpec(sourceId, sourceType, chart, modelRefId)
				.then( spec => {
					if (!spec.length) {
						return Promise.reject( Error(`Unable to create dashboard due to unsupported chart type: ${chart.chartType}`) )
					}

					DashboardSpec.fixupLiveWidgetSpec(spec[0], modelRefId, chart)
					return spec[0]
				})
				.then( spec => ({ spec, chart }) )
		)).then( chartInfoList => {
			chartInfoList = chartInfoList.filter(({ spec }) => !!spec )
			this._addLayoutInfo(chartInfoList, hasPredict, isPrimaryGroup )
			return chartInfoList
		})
	},

	_addLayoutInfo( chartInfoList, hasPredict, isPrimaryGroup){
		if( isPrimaryGroup ) {
			return this._addStyleForPrimaryGroup(chartInfoList, hasPredict)
		}
		/// Add style for secondary group
		const left = 56
		const width = 43
		const border = 1
		const noOfCharts = chartInfoList.length
		const height = 75 / noOfCharts - border

		chartInfoList.forEach((chartInfo, rowIndex) => {
			let top = rowIndex * height + (rowIndex + 1) * border
			chartInfo.style =  { left: `${left}%`, width: `${width}%`, height:`${height}%`, top:`${top}%` }
		})

		return chartInfoList
	},

	_addStyleForPrimaryGroup(charts, hasPredict ){
		const left = '1%'
		const width = '54%'
		const styles = {
			vizOnly: { viz: { top: '1%', height: '74%', left, width } },
			withText: { viz: { top: '1%', height: '57%', left, width }, text: { top: '60%', height: '15%', left, width } },
			withPredict: { viz: { top: '1%', height: '56%', left, width }, predict: { top: '58%', height: '17%', left, width } },
			withBoth: {
				predict: { top: '1%', height: '18%', left, width },
				viz: { top: '20%', height: '35%', left, width },
				text: { top: '65%', height: '10%', left, width }
			}
		}
		let styleToUse = {}
		if (charts.length === 1) {
			styleToUse = styles.vizOnly
		} else if (charts.length === 2) {
			styleToUse = hasPredict ? styles.withPredict : styles.withText
		} else {
			styleToUse = styles.withBoth
		}

		charts.forEach( chartInfo => {
			const { chart } = chartInfo
			switch(chart.chartType) {
				case 'Spiral':
				case 'DecisionTree':
				case 'DriverAnalysis':
				case 'Sunburst':
					chartInfo.style = styleToUse.predict
					break
				default:
					chartInfo.style = styleToUse.viz
					break
			}
		})

		return charts
	},

	/**
	 * Convert viz recommendations to livewidget specs
	 *
	 * @param  {string} sourceId
	 * @param  {string} sourceType
	 * @param  {object} recommendations
	 * @return {promise} - (specs)
	 */
	_convertToLiveWidgetSpec(sourceId, sourceType, chartRec) {
		if( !chartRec ){
			return
		}
		return this.glassContext.getSvc('.LiveWidgetService')
			.then(svc => svc.transformRecommendationsToLiveWidgetSpecs(
				[chartRec], {
					assetId: sourceId,
					type: sourceType
				}
			))
	}
}